'use client';

import { ReactNode, Suspense } from 'react';

import { Map } from 'mapbox-gl';
import { NuqsAdapter } from 'nuqs/adapters/next/app';
import * as Sentry from '@sentry/nextjs';

import { AnalyticsProvider } from '@domain/analytics/AnalyticsContext';
import { AuthProvider } from '@domain/auth/AuthContext';
import { CookiePermissionsProvider } from '@domain/cookies/CookiePermissionsContext';
import { GeolocationProvider } from '@domain/geolocation/GeolocationContext';
import { SessionProvider } from '@domain/session/SessionContext';
import { _hasAccessToken } from '@domain/auth/tokenStorage';
import { hasCookies, initialiseCookies } from '@lib/cookies/cookies';
import { IEvent } from '@generated/events';
import { ApolloWrapper } from '../ApolloWrapper/ApolloWrapper';
import { ClientSideInitializer } from '../ClientSideInitializer/ClientSideInitializer';
import { PageViewHandler } from '../PageViewHandler/PageViewHandler';
import { GoogleTagManager } from '../Scripts/GoogleTagManager/GoogleTagManager';
import { Hotjar } from '../Scripts/Hotjar/Hotjar';
import { ClientError } from '@/views/ClientError/ClientError';

/**
 * The ClientAppLogic component is responsible for rendering the
 * client-side logic of the application. It wraps the entire
 * application with various context providers and handles error boundaries.
 *
 * Can be wrapped by the LayoutWrapper which is a server component
 * or can be used without the layout as in the case of app pages.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {ReactNode} [props.children] - The child components to be rendered.
 * @param {boolean} [props.blockDatadogRum=false] - Whether to block Datadog RUM.
 * @returns {JSX.Element} The rendered component.
 */

interface IProps {
  children?: ReactNode;
}

interface ReCaptchaEnterpriseInstance {
  /**
   * Executes the reCAPTCHA Enterprise check and returns a token
   * @param siteKey - The reCAPTCHA Enterprise site key
   * @param options - Options including the action name
   * @returns A promise that resolves with the token string
   */
  execute(siteKey: string, options: { action: string }): Promise<string>;

  /**
   * Programmatically invoke the reCAPTCHA Enterprise challenge
   */
  ready(callback: () => void): void;
}

declare global {
  interface Window {
    __APOLLO_STATE__: object;
    __BUILDER_APOLLO_STATE__: object;
    __BUILDER_IO_APOLLO_STATE__: object;
    __HAS_AUTH_TOKEN: boolean;
    __PENDING_EVENTS__: IEvent[];
    dataLayer?: object[];
    exploreMap?: Map;
    fbq?: (...args: any) => void; // Facebook pixel
    gapi: any;
    gtag?: (...args: any) => void; // Google Tag Manager
    Osano?: import('@app-types/osano').Osano; // Cookie consent
    zE?: (...args: any) => void; // Zendesk
    grecaptcha?: { enterprise?: ReCaptchaEnterpriseInstance };
  }
}

// Initialise cookies. Not in a useEffect hook as we need it before the first render
// NB: For server components this will not instantiate the cookies
if (!hasCookies()) {
  initialiseCookies();
}

export const ClientAppLogic = ({ children }: IProps) => {
  return (
    <Sentry.ErrorBoundary fallback={<ClientError />}>
      <NuqsAdapter>
        <GeolocationProvider>
          <CookiePermissionsProvider>
            <Suspense>
              <GoogleTagManager />
            </Suspense>
            <Hotjar />
            <ApolloWrapper>
              <AnalyticsProvider>
                {/* We start off unauthenticated by default and let the authentication be updated on the client */}
                <AuthProvider isAuthenticated={_hasAccessToken()}>
                  <SessionProvider>
                    <Suspense>
                      <ClientSideInitializer />
                      <PageViewHandler />
                    </Suspense>
                    {children}
                  </SessionProvider>
                </AuthProvider>
              </AnalyticsProvider>
            </ApolloWrapper>
          </CookiePermissionsProvider>
        </GeolocationProvider>
      </NuqsAdapter>
    </Sentry.ErrorBoundary>
  );
};
